(() => {
  const METHOD_CONFIG = {
    espresso: {
      id: 'espresso',
      name: 'Espresso',
      ratio: 2,
      temp: '94C',
      grind: 'Fine (Table Salt)',
      steps: [
        'Purge your group head and wipe your portafilter basket completely dry before dosing.',
        'Dose your coffee, distribute the grounds evenly, and tamp with firm, level pressure.',
        'Lock in the portafilter and start the shot immediately to avoid scorching the puck.',
        'Aim for a 1:2 ratio - e.g. 18g in, 36g out - over 28-32 seconds for a balanced, syrupy shot.',
        'Gently stir to incorporate the crema before drinking for a consistent flavour throughout.',
      ],
    },
    cafetiere: {
      id: 'cafetiere',
      name: 'Cafetiere',
      ratio: 18,
      temp: '96C',
      grind: 'Coarse (Sea Salt)',
      steps: [
        'Preheat the cafetiere with hot water, then discard. Add your coarsely ground coffee.',
        'Pour all of your water in a steady stream, ensuring all grounds are fully saturated.',
        'Let it steep for 6 minutes, then gently break the crust of floating grounds with a spoon.',
        'Scoop off any remaining floating foam or fragments for a cleaner, lighter body.',
        'Place the lid on and plunge slowly with light pressure. Pour immediately to stop extraction.',
      ],
    },
    pourover: {
      id: 'pourover',
      name: 'Pour Over',
      ratio: 16.7,
      temp: '93C',
      grind: 'Medium-Fine (Table Salt)',
      steps: [
        'Rinse the paper filter with hot water to remove paper taste and preheat the dripper, then discard.',
        'Add coffee and pour about twice the coffee weight in water. Wait 30-40 seconds for the bloom.',
        'Pour the remaining water in slow, concentric circles, avoiding the very edge of the filter.',
        'Gently swirl or stir once to knock grounds off the sides and level the coffee bed.',
        'Total brew time should be around 3:30. Remove the filter and enjoy a clean, vibrant cup.',
      ],
    },
    aeropress: {
      id: 'aeropress',
      name: 'AeroPress',
      ratio: 14,
      temp: '93C',
      grind: 'Medium-Fine (Fine Sand)',
      steps: [
        'Rinse the paper filter in the cap, attach to the chamber, and place on a sturdy mug.',
        'Add coffee, then pour all of the water quickly. Stir gently for about 10 seconds.',
        'Insert the plunger slightly to create a vacuum seal and let it steep for 2 minutes.',
        'Remove the plunger, give one last gentle stir, then plunge slowly for 20-30 seconds.',
        'Stop when you hear a soft hiss. Top up with hot water if you prefer a longer, lighter cup.',
      ],
    },
  };

  const STRENGTH_CONFIG = {
    mellow: {
      id: 'mellow',
      label: 'Mellow',
      mod: 1.1,
    },
    balanced: {
      id: 'balanced',
      label: 'Balanced',
      mod: 1,
    },
    bold: {
      id: 'bold',
      label: 'Bold',
      mod: 0.9,
    },
  };

  const LIMITS = {
    pageMin: 1,
    pageMax: 3,
    cupsMin: 1,
    cupsMax: 8,
    doseMin: 7,
    doseMax: 22,
  };

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const roundHalf = (value) => Math.round(value * 2) / 2;

  const formatValue = (value) => {
    if (!Number.isFinite(value)) return '0';
    return Number.isInteger(value) ? String(value) : String(value.toFixed(1)).replace(/\.0$/, '');
  };

  class WBCBrewCalculator {
    constructor(root) {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.wbcBrewInit === 'true') return;
      root.dataset.wbcBrewInit = 'true';

      this.root = root;

      this.defaultMethod = Object.prototype.hasOwnProperty.call(METHOD_CONFIG, root.dataset.defaultMethod)
        ? root.dataset.defaultMethod
        : 'espresso';
      this.defaultStrength = Object.prototype.hasOwnProperty.call(STRENGTH_CONFIG, root.dataset.defaultStrength)
        ? root.dataset.defaultStrength
        : 'balanced';
      this.defaultCups = clamp(Math.round(toNumber(root.dataset.defaultCups, 1)), LIMITS.cupsMin, LIMITS.cupsMax);
      this.defaultDose = clamp(roundHalf(toNumber(root.dataset.defaultDose, 18)), LIMITS.doseMin, LIMITS.doseMax);
      this.cupSizeMl = Math.max(100, toNumber(root.dataset.cupSize, 250));

      this.state = {
        page: 1,
        methodId: this.defaultMethod,
        strengthId: this.defaultStrength,
        cups: this.defaultCups,
        dose: this.defaultDose,
      };

      this.cacheElements();
      this.copyButtonDefaultText = this.copyButton ? this.copyButton.textContent.trim() || 'Copy Recipe' : 'Copy Recipe';
      this.copyStatusTimer = null;
      this.bindEvents();
      this.render();
    }

    cacheElements() {
      this.progressBar = this.root.querySelector('.wbc-brew-calc__progress');
      this.pages = Array.from(this.root.querySelectorAll('[data-page]'));
      this.dots = Array.from(this.root.querySelectorAll('[data-progress-dot]'));
      this.methodButtons = Array.from(this.root.querySelectorAll('[data-method-id]'));
      this.strengthButtons = Array.from(this.root.querySelectorAll('[data-strength-id]'));

      this.generateButton = this.root.querySelector('[data-action="generate"]');
      this.resetButton = this.root.querySelector('[data-action="reset"]');
      this.copyButton = this.root.querySelector('[data-action="copy"]');
      this.shareButton = this.root.querySelector('[data-action="share"]');
      this.shareLabel = this.root.querySelector('[data-share-label]');
      this.backToMethodsButton = this.root.querySelector('[data-action="back-to-methods"]');
      this.backToStrengthButton = this.root.querySelector('[data-action="back-to-strength"]');

      this.doseMinusButton = this.root.querySelector('[data-action="dose-minus"]');
      this.dosePlusButton = this.root.querySelector('[data-action="dose-plus"]');
      this.cupsMinusButton = this.root.querySelector('[data-action="cups-minus"]');
      this.cupsPlusButton = this.root.querySelector('[data-action="cups-plus"]');

      this.doseRow = this.root.querySelector('[data-dose-row]');
      this.cupsRow = this.root.querySelector('[data-cups-row]');

      this.doseValue = this.root.querySelector('[data-field="dose"]');
      this.cupsValue = this.root.querySelector('[data-field="cups"]');
      this.recipeHeading = this.root.querySelector('[data-field="recipe-heading"]');
      this.coffeeValue = this.root.querySelector('[data-field="coffee"]');
      this.waterLabel = this.root.querySelector('[data-field="water-label"]');
      this.waterValue = this.root.querySelector('[data-field="water"]');
      this.grindValue = this.root.querySelector('[data-field="grind"]');
      this.tempValue = this.root.querySelector('[data-field="temp"]');
      this.stepsContainer = this.root.querySelector('[data-field="steps"]');

      this.liveRegion = this.root.querySelector('[data-live-region]');
    }

    bindEvents() {
      this.methodButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const methodId = button.dataset.methodId;
          if (!Object.prototype.hasOwnProperty.call(METHOD_CONFIG, methodId)) return;
          this.state.methodId = methodId;
          this.state.page = 2;
          this.render();
        });
      });

      this.strengthButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const strengthId = button.dataset.strengthId;
          if (!Object.prototype.hasOwnProperty.call(STRENGTH_CONFIG, strengthId)) return;
          this.state.strengthId = strengthId;
          this.render();
        });
      });

      if (this.generateButton) {
        this.generateButton.addEventListener('click', () => {
          this.state.page = 3;
          this.render();
        });
      }

      if (this.resetButton) {
        this.resetButton.addEventListener('click', () => {
          this.state.methodId = this.defaultMethod;
          this.state.strengthId = this.defaultStrength;
          this.state.cups = this.defaultCups;
          this.state.dose = this.defaultDose;
          this.state.page = 1;
          this.announce('Calculator reset.');
          this.render();
        });
      }

      if (this.copyButton) {
        this.copyButton.addEventListener('click', () => {
          this.copyRecipe();
        });
      }

      if (this.shareButton) {
        this.shareButton.addEventListener('click', () => {
          this.shareRecipe();
        });
      }

      if (this.backToMethodsButton) {
        this.backToMethodsButton.addEventListener('click', () => {
          this.state.page = 1;
          this.render();
        });
      }

      if (this.backToStrengthButton) {
        this.backToStrengthButton.addEventListener('click', () => {
          this.state.page = 2;
          this.render();
        });
      }

      if (this.doseMinusButton) {
        this.doseMinusButton.addEventListener('click', () => {
          this.state.dose = clamp(roundHalf(this.state.dose - 0.5), LIMITS.doseMin, LIMITS.doseMax);
          this.render();
        });
      }

      if (this.dosePlusButton) {
        this.dosePlusButton.addEventListener('click', () => {
          this.state.dose = clamp(roundHalf(this.state.dose + 0.5), LIMITS.doseMin, LIMITS.doseMax);
          this.render();
        });
      }

      if (this.cupsMinusButton) {
        this.cupsMinusButton.addEventListener('click', () => {
          this.state.cups = clamp(Math.round(this.state.cups - 1), LIMITS.cupsMin, LIMITS.cupsMax);
          this.render();
        });
      }

      if (this.cupsPlusButton) {
        this.cupsPlusButton.addEventListener('click', () => {
          this.state.cups = clamp(Math.round(this.state.cups + 1), LIMITS.cupsMin, LIMITS.cupsMax);
          this.render();
        });
      }
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.liveRegion.textContent = message;
      }, 10);
    }

    setCopyButtonState(state) {
      if (!this.copyButton) return;
      if (this.copyStatusTimer) {
        window.clearTimeout(this.copyStatusTimer);
        this.copyStatusTimer = null;
      }

      this.copyButton.classList.remove('is-copied', 'is-error');

      if (state === 'success') {
        this.copyButton.textContent = 'Copied';
        this.copyButton.classList.add('is-copied');
        this.copyStatusTimer = window.setTimeout(() => {
          this.setCopyButtonState('default');
        }, 1800);
        return;
      }

      if (state === 'error') {
        this.copyButton.textContent = 'Copy failed';
        this.copyButton.classList.add('is-error');
        this.copyStatusTimer = window.setTimeout(() => {
          this.setCopyButtonState('default');
        }, 2200);
        return;
      }

      this.copyButton.textContent = this.copyButtonDefaultText;
    }

    setShareButtonState(state) {
      if (!this.shareButton || !this.shareLabel) return;

      if (this.shareStatusTimer) {
        window.clearTimeout(this.shareStatusTimer);
        this.shareStatusTimer = null;
      }

      this.shareButton.classList.remove('is-success', 'is-copied', 'is-error');

      if (state === 'success') {
        this.shareLabel.textContent = 'Shared';
        this.shareButton.classList.add('is-success');
        this.shareStatusTimer = window.setTimeout(() => {
          this.setShareButtonState('default');
        }, 1800);
        return;
      }

      if (state === 'copied') {
        this.shareLabel.textContent = 'Share text copied';
        this.shareButton.classList.add('is-copied');
        this.shareStatusTimer = window.setTimeout(() => {
          this.setShareButtonState('default');
        }, 2200);
        return;
      }

      if (state === 'error') {
        this.shareLabel.textContent = 'Share failed';
        this.shareButton.classList.add('is-error');
        this.shareStatusTimer = window.setTimeout(() => {
          this.setShareButtonState('default');
        }, 2200);
        return;
      }

      this.shareLabel.textContent = 'Share';
    }

    getMethod() {
      return METHOD_CONFIG[this.state.methodId] || METHOD_CONFIG.espresso;
    }

    getStrength() {
      return STRENGTH_CONFIG[this.state.strengthId] || STRENGTH_CONFIG.balanced;
    }

    getRecipe() {
      const method = this.getMethod();
      const strength = this.getStrength();

      if (method.id === 'espresso') {
        return {
          coffee: this.state.dose,
          water: roundHalf(this.state.dose * method.ratio),
          waterLabel: 'Yield',
          grind: method.grind,
          temp: method.temp,
          steps: method.steps,
        };
      }

      const totalWater = this.state.cups * this.cupSizeMl;
      const coffee = roundHalf(totalWater / (method.ratio * strength.mod));

      return {
        coffee,
        water: totalWater,
        waterLabel: 'Water',
        grind: method.grind,
        temp: method.temp,
        steps: method.steps,
      };
    }

    render() {
      this.state.page = clamp(Math.round(this.state.page), LIMITS.pageMin, LIMITS.pageMax);

      if (this.state.page !== 3) {
        this.setCopyButtonState('default');
        this.setShareButtonState('default');
      }

      if (this.progressBar) {
        this.progressBar.setAttribute('aria-valuenow', String(this.state.page));
      }

      this.pages.forEach((pageEl) => {
        const pageNumber = Number(pageEl.dataset.page);
        pageEl.hidden = pageNumber !== this.state.page;
      });

      this.dots.forEach((dot) => {
        const dotPage = Number(dot.dataset.progressDot);
        dot.classList.toggle('is-active', dotPage === this.state.page);
      });

      this.methodButtons.forEach((button) => {
        const isActive = button.dataset.methodId === this.state.methodId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.strengthButtons.forEach((button) => {
        const isActive = button.dataset.strengthId === this.state.strengthId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const isEspresso = this.state.methodId === 'espresso';
      if (this.doseRow) this.doseRow.hidden = !isEspresso;
      if (this.cupsRow) this.cupsRow.hidden = isEspresso;

      if (this.doseValue) this.doseValue.textContent = `${formatValue(this.state.dose)}g`;
      if (this.cupsValue) this.cupsValue.textContent = String(this.state.cups);

      const method = this.getMethod();
      const strength = this.getStrength();
      const recipe = this.getRecipe();

      if (this.recipeHeading) this.recipeHeading.textContent = `${method.name} - ${strength.label}`;
      if (this.coffeeValue) this.coffeeValue.textContent = formatValue(recipe.coffee);
      if (this.waterLabel) this.waterLabel.textContent = recipe.waterLabel;
      if (this.waterValue) this.waterValue.textContent = formatValue(recipe.water);
      if (this.grindValue) this.grindValue.textContent = recipe.grind;
      if (this.tempValue) this.tempValue.textContent = recipe.temp;

      if (this.stepsContainer) {
        this.stepsContainer.innerHTML = '';
        recipe.steps.forEach((stepText, index) => {
          const item = document.createElement('div');
          item.className = 'wbc-brew-calc__step-item';

          const number = document.createElement('div');
          number.className = 'wbc-brew-calc__step-num';
          number.textContent = String(index + 1);

          const text = document.createElement('p');
          text.className = 'wbc-brew-calc__step-text';
          text.textContent = stepText;

          item.appendChild(number);
          item.appendChild(text);
          this.stepsContainer.appendChild(item);
        });
      }
    }

    copyRecipe() {
      const copyText = this.buildRecipeText({ includeShareIntro: false });
      this.trackCopyEvent('attempt', 'button');

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard
          .writeText(copyText)
          .then(() => {
            this.announce('Recipe copied to clipboard.');
            this.setCopyButtonState('success');
            this.trackCopyEvent('copied', 'clipboard_api');
          })
          .catch(() => {
            const copied = this.fallbackCopy(copyText);
            this.setCopyButtonState(copied ? 'success' : 'error');
            this.trackCopyEvent(copied ? 'copied' : 'failed', 'exec_command');
          });
        return;
      }

      const copied = this.fallbackCopy(copyText);
      this.setCopyButtonState(copied ? 'success' : 'error');
      this.trackCopyEvent(copied ? 'copied' : 'failed', 'exec_command');
    }

    getShareUrl() {
      const { origin, pathname } = window.location;
      return `${origin}${pathname}`;
    }

    buildRecipeText({ includeShareIntro = false } = {}) {
      const method = this.getMethod();
      const strength = this.getStrength();
      const recipe = this.getRecipe();
      const instructionLines = recipe.steps.map((step, index) => `${index + 1}. ${step}`);

      const lines = [];
      if (includeShareIntro) {
        lines.push(
          `Just dialled in this ${method.name} recipe with Wrexham Bean Co. Give it a brew, then build your own here: ${this.getShareUrl()}`,
          ''
        );
      }

      lines.push(
        `Wrexham Bean Co. ${method.name} Recipe`,
        `Strength: ${strength.label}`,
        `Coffee: ${formatValue(recipe.coffee)}g`,
        `${recipe.waterLabel}: ${formatValue(recipe.water)}ml`,
        `Grind: ${recipe.grind}`,
        `Temp: ${recipe.temp}`,
        '',
        'Method:',
        ...instructionLines
      );

      return lines.join('\n');
    }

    trackShareEvent(outcome, channel) {
      const method = this.getMethod();
      const strength = this.getStrength();
      const payload = {
        method_id: method.id,
        method_name: method.name,
        strength_id: strength.id,
        strength_label: strength.label,
        share_outcome: outcome,
        share_channel: channel,
        page_path: window.location.pathname,
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'brew_recipe_share', payload);
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'brew_recipe_share',
          ...payload,
        });
      }
    }

    trackCopyEvent(outcome, channel) {
      const method = this.getMethod();
      const strength = this.getStrength();
      const payload = {
        method_id: method.id,
        method_name: method.name,
        strength_id: strength.id,
        strength_label: strength.label,
        copy_outcome: outcome,
        copy_channel: channel,
        page_path: window.location.pathname,
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'brew_recipe_copy', payload);
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'brew_recipe_copy',
          ...payload,
        });
      }
    }

    shareRecipe() {
      const shareText = this.buildRecipeText({ includeShareIntro: true });
      const method = this.getMethod();
      const shareUrl = this.getShareUrl();

      this.trackShareEvent('attempt', 'button');

      if (navigator.share && typeof navigator.share === 'function') {
        navigator
          .share({
            title: `Wrexham Bean Co. ${method.name} Recipe`,
            text: shareText,
            url: shareUrl,
          })
          .then(() => {
            this.announce('Recipe shared.');
            this.setShareButtonState('success');
            this.trackShareEvent('shared', 'native');
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') {
              this.setShareButtonState('default');
              this.trackShareEvent('dismissed', 'native');
              return;
            }

            const copied = this.fallbackCopy(shareText);
            if (copied) {
              this.setShareButtonState('copied');
              this.announce('Share text copied to clipboard.');
              this.trackShareEvent('copied', 'clipboard');
            } else {
              this.setShareButtonState('error');
              this.trackShareEvent('failed', 'native');
            }
          });
        return;
      }

      const copied = this.fallbackCopy(shareText);
      if (copied) {
        this.setShareButtonState('copied');
        this.announce('Share text copied to clipboard.');
        this.trackShareEvent('copied', 'clipboard');
      } else {
        this.setShareButtonState('error');
        this.trackShareEvent('failed', 'clipboard');
      }
    }

    fallbackCopy(text) {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', 'readonly');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      area.style.pointerEvents = 'none';
      document.body.appendChild(area);
      area.focus();
      area.select();

      try {
        document.execCommand('copy');
        this.announce('Recipe copied to clipboard.');
        return true;
      } catch (error) {
        this.announce('Copy failed. Please copy manually.');
        return false;
      } finally {
        document.body.removeChild(area);
      }
    }
  }

  const init = (scope = document) => {
    const calculators = scope.querySelectorAll('[data-wbc-brew-calc]');
    calculators.forEach((root) => {
      new WBCBrewCalculator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
